import { useRef, useState } from 'react'
import { CloseIcon, PlusIcon } from '../icons/icons'
import { useFieldDensity } from './fieldDensity'

interface DateListInputProps {
  /** ISO dates (YYYY-MM-DD), kept sorted. */
  values: string[]
  onChange: (values: string[]) => void
  disabled?: boolean
  ariaLabel: string
  ariaInvalid?: boolean
  ariaRequired?: boolean
  onBlur?: () => void
  className?: string
}

/**
 * A list of specific dates as chips, plus an add pill that opens the native
 * date picker. Dates dedupe and stay sorted.
 */
export default function DateListInput({
  values,
  onChange,
  disabled = false,
  ariaLabel,
  ariaInvalid,
  ariaRequired,
  onBlur,
  className = '',
}: DateListInputProps) {
  const compact = useFieldDensity() === 'compact'
  const [draft, setDraft] = useState('')
  const [adding, setAdding] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const chipHeightClass = compact ? 'h-5' : 'h-[22px]'

  const commitDraft = (value: string) => {
    setDraft(value)
    // A native date input reports '' until the date is complete.
    if (!value) return
    onChange([...new Set([...values, value])].sort())
    setDraft('')
  }

  const openAdd = () => {
    if (disabled) return
    setAdding(true)
    const input = inputRef.current
    input?.focus()
    try {
      input?.showPicker?.()
    } catch {
      // Focus alone is enough where showPicker is gated (Chrome needs a user activation).
    }
  }

  return (
    <div
      className={`flex w-full flex-wrap items-center gap-1 rounded border px-2 focus-within:border-[color:var(--accent-info)] ${
        compact ? 'min-h-[32px] py-1' : 'min-h-9 py-1.5'
      } ${ariaInvalid ? 'border-danger' : 'border-default'} ${
        disabled ? 'bg-muted opacity-70' : 'surface-default'
      } ${className}`}
    >
      {values.map((date) => (
        <span
          key={date}
          className={`inline-flex ${chipHeightClass} max-w-full items-center gap-1 rounded border border-[color:var(--border-info)] bg-[color:var(--surface-info-subtle)] px-1.5 text-[11px] tabular-nums text-[color:var(--accent-info)]`}
        >
          <span className="truncate">{date}</span>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange(values.filter((candidate) => candidate !== date))}
            className="inline-flex shrink-0 text-muted transition-colors hover:text-default disabled:cursor-not-allowed"
            aria-label={`Remove ${date}`}
          >
            <CloseIcon size={9} aria-hidden="true" />
          </button>
        </span>
      ))}
      {adding ? null : (
        <button
          type="button"
          disabled={disabled}
          onClick={openAdd}
          aria-label={`Add to ${ariaLabel}`}
          className={`inline-flex ${chipHeightClass} shrink-0 items-center justify-center rounded border border-dashed border-[color:var(--border-strong)] px-1.5 ${disabled ? 'text-faint' : 'text-icon-quiet'} transition-colors hover:border-[color:var(--border-emphasis)] hover:text-data disabled:cursor-not-allowed`}
        >
          <PlusIcon size={10} aria-hidden="true" />
        </button>
      )}
      {/* Always mounted so tabbing in opens the add state; collapsed while resting. */}
      <input
        ref={inputRef}
        type="date"
        value={draft}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid || undefined}
        aria-required={ariaRequired}
        onFocus={() => setAdding(true)}
        onChange={(event) => commitDraft(event.target.value)}
        onBlur={() => {
          setAdding(false)
          setDraft('')
          onBlur?.()
        }}
        className={
          adding
            ? `min-w-[150px] flex-1 border-0 bg-transparent px-1 leading-5 font-[450] text-default outline-none disabled:cursor-not-allowed ${
                compact ? 'text-sm' : 'text-base'
              }`
            : 'h-0 w-0 min-w-0 flex-none appearance-none border-0 bg-transparent p-0 opacity-0'
        }
      />
    </div>
  )
}
